/**
 * TME Product PDF Download
 * Handles "Download PDF (Full)" and "Download Spec Sheet PDF" buttons.
 * Uses jsPDF (loaded from CDN). No external services required.
 *
 * INSTALL: In theme.liquid just before </body>:
 *   {% if template.name == 'product' %}
 *     <script src="{{ 'tme-pdf-download.js' | asset_url }}" defer></script>
 *   {% endif %}
 */

(function () {
  'use strict';

  // ─── Configuration ────────────────────────────────────────────────────────
  const CONFIG = {
    colors: {
      headerBg:    '#1a1a1a',
      headerText:  '#ffffff',
      sectionBg:   '#eeeeee',
      sectionText: '#1a1a1a',
      rowAlt:      '#f9f9f9',
      border:      '#dddddd',
      bodyText:    '#333333',
      accent:      '#8b6f47',
    },
    page: {
      format:      'a4',
      orientation: 'portrait',
      marginX:     14,
      marginTop:   18,
      marginBot:   14,
    },
  };

  // ─── CDN Library Loader ───────────────────────────────────────────────────
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector('script[src="' + src + '"]')) { resolve(); return; }
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  async function ensureLibraries() {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
    await loadMontserrat();
  }

  async function loadMontserrat() {
    if (window.__tmeMontserratLoaded) return;
    const { jsPDF } = window.jspdf;
    try {
      const response = await fetch('https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Hw5aXo.woff2');
      const buffer = await response.arrayBuffer();
      const base64 = btoa(Array.from(new Uint8Array(buffer)).map(b => String.fromCharCode(b)).join(''));
      const doc = new jsPDF();
      doc.addFileToVFS('Montserrat-Regular.ttf', base64);
      doc.addFont('Montserrat-Regular.ttf', 'Montserrat', 'normal');
      window.__tmeMontserratLoaded = true;
      console.log('[TME PDF] Montserrat loaded:', doc.getFontList()['Montserrat']);
    } catch(e) {
      console.warn('[TME PDF] Montserrat failed to load, using helvetica');
      window.__tmeMontserratLoaded = false;
    }
  }


  // ─── Accordion Helper ─────────────────────────────────────────────────────
  function withAccordionsOpen(callback) {
    const details = Array.from(document.querySelectorAll('details'));
    const wasOpen = details.map(d => d.open);
    details.forEach(d => { d.open = true; });
    let result;
    try {
      result = callback();
    } finally {
      details.forEach((d, i) => { d.open = wasOpen[i]; });
    }
    return result;
  }

  // ─── Data Getters ─────────────────────────────────────────────────────────
  function slugify(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function getProductTitle() {
    const el = document.querySelector('h1.product-title, .product-title, h1');
    return el ? el.textContent.trim() : document.title.split('|')[0].trim();
  }

  function getProductPrice() {
    const allPriceEls = document.querySelectorAll('[class*="price"]');
    for (const el of allPriceEls) {
      const directText = Array.from(el.childNodes)
        .filter(n => n.nodeType === 3)
        .map(n => n.textContent.trim())
        .join('').trim();
      if (directText && directText.match(/^[$£€][\d,]+\.?\d*$/)) {
        return directText;
      }
    }
    const priceEl = document.querySelector('.price');
    if (priceEl) {
      const match = priceEl.textContent.match(/[$£€][\d,]+\.?\d*/);
      if (match) return match[0];
    }
    return '';
  }

  function getProductVendor() {
    const selectors = [
      '.style_vendor',
      '.product-vendor',
      '.product__vendor',
      '[class*="style_vendor"]',
      '[class*="product--text"][class*="vendor"]',
      '[class*="vendor"]',
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el && el.textContent.trim()) return el.textContent.trim().toUpperCase();
    }
    return '';
  }

  function getProductDescription() {
    const BAD_MARKERS = ['Name Email Phone', 'hCaptcha', 'Add to cart', 'Sold out'];
    const candidates = [
      '.rich-text__inner',
      '.product-short-description',
      '.product__description',
      '.product-description',
    ];
    for (const sel of candidates) {
      const els = document.querySelectorAll(sel);
      for (const el of els) {
        const text = el.innerText.trim();
        if (text.length < 20) continue;
        if (BAD_MARKERS.some(m => text.includes(m))) continue;
        return text;
      }
    }
    return '';
  }

  function getBodyText() {
    // Body text lives in .hcimagevideo columns that don't contain a video
    const container = document.querySelector('.hcimagevideo');
    if (!container) return '';
    const cols = container.querySelectorAll('.hcsubtable');
    const textParts = [];
    cols.forEach(function(col) {
      // Skip columns that contain a video/iframe
      if (col.querySelector('iframe, [data-src*="youtube"], [src*="youtube"], lite-youtube')) return;
      const text = col.innerText.trim();
      if (text.length > 30) textParts.push(text);
    });
    return textParts.join('\n\n');
  }

  function getSpecSections() {
    // Specs use custom .hcsubtable divs from the product-spec-table.liquid section.
    // Structure: .hcsubtable > h4 (heading) + .hctables > .hcrow > .hccolumn1 + .hccolumn2
    const sections = [];
    document.querySelectorAll('.hcsubtable').forEach(function(subtable) {
      const headingEl = subtable.querySelector('h4');
      const heading = headingEl ? headingEl.textContent.trim() : '';
      const rows = [];
      subtable.querySelectorAll('.hcrow').forEach(function(row) {
        const col1 = row.querySelector('.hccolumn1');
        const col2 = row.querySelector('.hccolumn2');
        if (col1 && col2) {
          const label = col1.textContent.trim();
          const value = col2.textContent.trim();
          if (label || value) rows.push({ label, value });
        }
      });
      if (rows.length) sections.push({ heading, rows });
    });
    return sections;
  }

  function getProductImages(maxImages) {
    const seen = new Set();
    const images = [];

    function isProductImage(src) {
      if (!src) return false;
      // Accept both standard Shopify CDN and store-proxied CDN (themusicemporium.com/cdn/shop/files/)
      return src.includes('cdn.shopify.com/s/files') || src.includes('/cdn/shop/files/');
    }

    function cleanImageUrl(src) {
      src = src.split('?')[0];
      src = src.replace(/_\d+x\d*\./, '.').replace(/_\d+x\./, '.');
      return src + '?width=1200';
    }

    document.querySelectorAll('img').forEach(img => {
      if (img.offsetWidth < 200 && img.offsetHeight < 200) return;
      if (img.closest('header, .site-header, nav, footer, .site-footer, .announcement-bar')) return;
      const candidates = [img.src, img.dataset.src, img.dataset.lazySrc];
      if (img.srcset) candidates.push(img.srcset.split(',')[0].trim().split(' ')[0]);
      for (const raw of candidates) {
        if (!raw || !isProductImage(raw)) continue;
        if (raw.includes('TME-logo') || raw.includes('logo-long') || raw.includes('logo_')) continue;
        const url = cleanImageUrl(raw);
        if (seen.has(url)) continue;
        seen.add(url);
        images.push({ url, naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
        break;
      }
    });

    return images.slice(0, maxImages || 8);
  }

  // ─── Image → DataURL ──────────────────────────────────────────────────────
  function fetchImageAsDataUrl(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width  = img.naturalWidth  || 800;
          canvas.height = img.naturalHeight || 600;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch (e) { reject(e); }
      };
      img.onerror = () => reject(new Error('Image load failed: ' + url));
      img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
    });
  }

  // ─── PDF Builder ──────────────────────────────────────────────────────────
  class TMEPdfBuilder {
    constructor(title) {
      const { jsPDF } = window.jspdf;
      this.doc     = new jsPDF({ orientation: CONFIG.page.orientation, unit: 'mm', format: CONFIG.page.format });
      this.title   = title;
      this.x       = CONFIG.page.marginX;
      this.pageW   = this.doc.internal.pageSize.getWidth();
      this.pageH   = this.doc.internal.pageSize.getHeight();
      this.cW      = this.pageW - CONFIG.page.marginX * 2;
      this.y       = CONFIG.page.marginTop;
      this.pageNum = 1;
    }

    needSpace(h) {
      if (this.y + h > this.pageH - CONFIG.page.marginBot - 10) {
        this.footer();
        this.doc.addPage();
        this.pageNum++;
        this.y = CONFIG.page.marginTop;
        this.miniHeader();
      }
    }

    font(style, size, color) {
      this.doc.setFont('helvetica', style || 'normal');
      this.doc.setFontSize(size || 10);
      this.doc.setTextColor(color || CONFIG.colors.bodyText);
    }

    hr(offset) {
      this.doc.setDrawColor(CONFIG.colors.border);
      this.doc.setLineWidth(0.3);
      this.doc.line(this.x, this.y + (offset || 0), this.x + this.cW, this.y + (offset || 0));
    }

    async coverHeader() {
      this.doc.setFillColor(CONFIG.colors.headerBg);
      this.doc.rect(0, 0, this.pageW, 30, 'F');

      const LOGO_B64 = 'data:image/png;base64,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';
      this.doc.addImage(LOGO_B64, 'PNG', this.x, 8, 58, 13);

      this.font('normal', 9, '#ffffff');
      this.doc.text('165 Massachusetts Ave. | Lexington, MA | 781-860-0049', this.pageW - this.x, 13, { align: 'right' });
      this.doc.text('themusicemporium.com', this.pageW - this.x, 20, { align: 'right' });
      this.y = 36;
    }

    miniHeader() {
      this.doc.setFillColor(CONFIG.colors.headerBg);
      this.doc.rect(0, 0, this.pageW, 11, 'F');
      this.font('bold', 9, '#ffffff');
      this.doc.text('THE MUSIC EMPORIUM', this.x, 7.5);
      this.font('normal', 9, '#ffffff');
      const short = this.title.length > 60 ? this.title.substring(0, 57) + '...' : this.title;
      this.doc.text(short, this.pageW - this.x, 7.5, { align: 'right' });
      this.y = 17;
    }

    footer() {
      const fy = this.pageH - 7;
      this.doc.setDrawColor(CONFIG.colors.border);
      this.doc.setLineWidth(0.25);
      this.doc.line(this.x, fy - 3, this.x + this.cW, fy - 3);
      this.font('normal', 7, '#555555');
      this.doc.text('themusicemporium.com  |  165 Massachusetts Ave., Lexington, MA  |  781-860-0049', this.x, fy);
      this.doc.text('Page ' + this.pageNum, this.pageW - this.x, fy, { align: 'right' });
    }

    titleBlock(price, vendor) {
      const pad = 7;
      const bodyFont  = 'helvetica';
      const titleFont = window.__tmeMontserratLoaded ? 'Montserrat' : 'helvetica';

      if (vendor) {
        this.doc.setFont(bodyFont, 'normal');
        this.doc.setFontSize(12);
        this.doc.setTextColor('#1a1a1a');
        const spaced = vendor.split('').join(' ');
        this.doc.text(spaced, this.x, this.y);
        this.y += 11;
      }

      this.doc.setFont(titleFont, 'normal');
      this.doc.setFontSize(16);
      this.doc.setTextColor('#1a1a1a');
      const lines = this.doc.splitTextToSize(this.title, this.cW);
      this.doc.text(lines, this.x, this.y);
      this.y += lines.length * 7 + 1;

      this.y += 4;

      if (price) {
        this.doc.setFont(bodyFont, 'normal');
        this.doc.setFontSize(14);
        this.doc.setTextColor('#1a1a1a');
        this.doc.text(price, this.x, this.y);
        this.y += 11;
      }

      this.y += 3;
    }

    description(text) {
      if (!text) return;
      this.needSpace(10);
      this.y += 6;

      const paragraphs = text.split(/\n{2,}/);
      paragraphs.forEach(para => {
        const trimmed = para.trim().replace(/\n/g, ' ');
        if (!trimmed) return;
        this.font('normal', 16, '#1a1a1a');
        const lines = this.doc.splitTextToSize(trimmed, this.cW);
        this.needSpace(lines.length * 4.5 + 4);
        this.doc.text(lines, this.x + this.cW / 2, this.y, { align: 'center', maxWidth: this.cW });
        this.y += lines.length * 4.5 + 3;
      });
      this.y += 12;
    }

    bodyText(text) {
      if (!text) return;
      const BAD = ['Name Email Phone', 'hCaptcha', 'Add to cart', 'Sold out'];
      if (BAD.some(m => text.includes(m))) return;
      this.y += 4;
      const paragraphs = text.split(/\n{2,}/);
      paragraphs.forEach(para => {
        const trimmed = para.trim().replace(/\n/g, ' ');
        if (!trimmed || trimmed.length < 10) return;
        this.font('normal', 9, '#333333');
        const lines = this.doc.splitTextToSize(trimmed, this.cW);
        this.needSpace(lines.length * 4.5 + 4);
        this.doc.text(lines, this.x, this.y);
        this.y += lines.length * 4.5 + 4;
      });
      this.y += 6;
    }

    specs(sections) {
      if (!sections || !sections.length) return;
      this.needSpace(20);
      this.font('bold', 8.5, '#555555');
      this.doc.text('SPECIFICATIONS', this.x, this.y);
      this.y += 4;
      this.hr();
      this.y += 5;

      sections.forEach(section => {
        this.needSpace(16);
        this.doc.setFillColor(CONFIG.colors.sectionBg);
        this.doc.rect(this.x, this.y - 4.5, this.cW, 7.5, 'F');
        this.font('bold', 8.5, CONFIG.colors.sectionText);
        this.doc.text((section.heading || 'Specifications').toUpperCase(), this.x + 3, this.y + 0.5);
        this.y += 7;

        const labelW = this.cW * 0.38;
        const valueW = this.cW * 0.62;
        const rowH   = 7;

        section.rows.forEach((row, i) => {
          this.needSpace(rowH + 1);
          if (i % 2 === 0) {
            this.doc.setFillColor(CONFIG.colors.rowAlt);
            this.doc.rect(this.x, this.y - 4.5, this.cW, rowH, 'F');
          }
          this.doc.setDrawColor(CONFIG.colors.border);
          this.doc.setLineWidth(0.15);
          this.doc.rect(this.x, this.y - 4.5, this.cW, rowH, 'S');
          this.font('bold', 7.5, '#444444');
          this.doc.text(row.label, this.x + 2.5, this.y + 0.5);
          this.font('normal', 7.5, CONFIG.colors.bodyText);
          const vLines = this.doc.splitTextToSize(row.value || '', valueW - 4);
          this.doc.text(vLines, this.x + labelW + 2, this.y + 0.5);
          this.y += rowH;
        });
        this.y += 5;
      });
    }

    async images(imageList) {
      if (!imageList || !imageList.length) return;
      this.needSpace(20);

      // Single image: render as a scaled-down 8.5x11 portrait (letter) proportions
if (imageList.length === 1) {
  const img0 = imageList[0];
  const ratio = 11 / 8.5; // letter portrait aspect ratio
  const maxH  = this.pageH - this.y - CONFIG.page.marginBot - 15;
  let imgW = this.cW;
  let imgH = imgW * ratio;
  // If it overflows the remaining page height, scale down to fit
  if (imgH > maxH) {
    imgH = maxH;
    imgW = imgH / ratio;
  }
  this.needSpace(imgH + 5);
  try {
    const data = await fetchImageAsDataUrl(img0.url);
    this.doc.addImage(data, 'JPEG', this.x, this.y, imgW, imgH, undefined, 'MEDIUM');
    this.doc.setDrawColor(CONFIG.colors.border);
    this.doc.setLineWidth(0.25);
    this.doc.rect(this.x, this.y, imgW, imgH, 'S');
  } catch (e) {
    this.doc.setFillColor('#f0f0f0');
    this.doc.rect(this.x, this.y, imgW, imgH, 'F');
    this.font('normal', 7, '#aaaaaa');
    this.doc.text('Image unavailable', this.x + imgW / 2, this.y + imgH / 2, { align: 'center' });
  }
  this.y += imgH + 4;
  return;
}

      // Multiple images: 2-column grid
      const cols = 2;
      const gap  = 4;
      const imgW = (this.cW - gap) / cols;
      const imgH = imgW * 0.72;
      let col    = 0;
      let rowY   = this.y;

      for (const img of imageList) {
        if (col === 0) {
          this.needSpace(imgH + gap + 5);
          rowY = this.y;
        }
        const imgX = this.x + col * (imgW + gap);
        try {
          const data = await fetchImageAsDataUrl(img.url);
          this.doc.addImage(data, 'JPEG', imgX, rowY, imgW, imgH, undefined, 'MEDIUM');
          this.doc.setDrawColor(CONFIG.colors.border);
          this.doc.setLineWidth(0.25);
          this.doc.rect(imgX, rowY, imgW, imgH, 'S');
        } catch (e) {
          this.doc.setFillColor('#f0f0f0');
          this.doc.rect(imgX, rowY, imgW, imgH, 'F');
          this.font('normal', 7, '#aaaaaa');
          this.doc.text('Image unavailable', imgX + imgW / 2, rowY + imgH / 2, { align: 'center' });
        }
        col++;
        if (col >= cols) {
          col = 0;
          this.y = rowY + imgH + gap;
        }
      }
      if (col > 0) this.y = rowY + imgH + gap;
      this.y += 2;
    }

    save(filename) {
      this.footer();
      this.doc.setProperties({ title: filename.replace('.pdf', '') });
      const blob = this.doc.output('blob');
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      a.target   = '_blank';
      a.rel      = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function() { URL.revokeObjectURL(url); }, 10000);
    }
  }

  // ─── PDF Generation ───────────────────────────────────────────────────────
  async function generateFullPDF() {
    const title    = getProductTitle();
    const price    = getProductPrice();
    const vendor   = getProductVendor();
    const desc     = getProductDescription();
    const sections = getSpecSections();
    const imgs     = getProductImages(1);

    console.log('[TME PDF] Full:', { title, price, vendor, descLen: desc.length, specSections: sections.length, images: imgs.length });

    const b = new TMEPdfBuilder(title);
    await b.coverHeader();
    b.titleBlock(price, vendor);
    await b.images(imgs);
    b.description(desc);
    const bodyText = getBodyText();
    if (bodyText) b.bodyText(bodyText);
    b.specs(sections);
    b.save(slugify(title) + '-full.pdf');
  }

  async function generateSpecPDF() {
    const title    = getProductTitle();
    const price    = getProductPrice();
    const vendor   = getProductVendor();
    const sections = getSpecSections();
    const imgs     = getProductImages(1);

    console.log('[TME PDF] Spec:', { title, price, vendor, specSections: sections.length, images: imgs.length });

    const b = new TMEPdfBuilder(title);
    await b.coverHeader();
    b.titleBlock(price, vendor);
    if (imgs.length) await b.images([imgs[0]]);
    b.specs(sections);
    b.save(slugify(title) + '-specs.pdf');
  }

  // ─── Button Handling ──────────────────────────────────────────────────────
  function setButtonLoading(btn, loading) {
    const span = btn.querySelector('span');
    if (loading) {
      btn.disabled = true;
      btn.dataset.orig = span ? span.textContent : btn.textContent;
      if (span) span.textContent = 'Generating PDF…';
      else btn.textContent = 'Generating PDF…';
    } else {
      btn.disabled = false;
      const orig = btn.dataset.orig || '';
      if (span && orig) span.textContent = orig;
      else if (orig) btn.textContent = orig;
    }
  }

  async function handleClick(e) {
    const btn  = e.currentTarget;
    const type = btn.dataset.tmePdf;
    setButtonLoading(btn, true);
    try {
      await ensureLibraries();
      if (type === 'full') await generateFullPDF();
      else await generateSpecPDF();
    } catch (err) {
      console.error('[TME PDF] Error:', err);
      alert('Sorry, there was a problem generating the PDF.\n\nError: ' + err.message);
    } finally {
      setButtonLoading(btn, false);
    }
  }

  // ─── Init ─────────────────────────────────────────────────────────────────
  const bound = new WeakSet();

  function bindButtons() {
    document.querySelectorAll('[data-tme-pdf]').forEach(btn => {
      if (!bound.has(btn)) {
        btn.addEventListener('click', handleClick);
        bound.add(btn);
        console.log('[TME PDF] Bound:', btn.dataset.tmePdf);
      }
    });
  }

  function init() {
    bindButtons();
    const obs = new MutationObserver(bindButtons);
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => obs.disconnect(), 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', bindButtons);

})();